import React, { useEffect, useRef, useState, useCallback } from "react";
import { StickyNote, Star, Pin, Plus, Search, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { smartSearch } from "../lib/search";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";

const COLORS = {
  amber: "bg-amber-100 border-amber-300 text-amber-950",
  blue: "bg-blue-100 border-blue-300 text-blue-950",
  green: "bg-emerald-100 border-emerald-300 text-emerald-950",
  pink: "bg-pink-100 border-pink-300 text-pink-950",
  purple: "bg-purple-100 border-purple-300 text-purple-950",
  orange: "bg-orange-100 border-orange-300 text-orange-950",
};
const DOT = { amber: "bg-amber-400", blue: "bg-blue-400", green: "bg-emerald-400", pink: "bg-pink-400", purple: "bg-purple-400", orange: "bg-orange-400" };
const COLOR_KEYS = Object.keys(COLORS);
const empty = { title: "", content: "", color: "amber", category: "", pinned: false, favorite: false };
const fmtDate = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("es-GT", { day: "2-digit", month: "short" }); } catch { return ""; } };

export function NotebookTab() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [isOpen, setIsOpen] = useState(() => localStorage.getItem("selec_notebook_open") === "1");
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const load = useCallback(() => {
    if (!localStorage.getItem("selec_token")) return;
    api.get("/notebook").then((r) => setNotes(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("notes:changed", h);
    return () => window.removeEventListener("notes:changed", h);
  }, [load]);

  const setOpen = (v) => { setIsOpen(v); localStorage.setItem("selec_notebook_open", v ? "1" : "0"); };

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e) => { if (dlg) return; if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isOpen, dlg]);

  if (!user) return null;

  const notify = () => window.dispatchEvent(new Event("notes:changed"));
  const openCreate = () => { setEditing(null); setForm(empty); setDlg(true); };
  const openEdit = (n) => { setEditing(n); setForm({ ...empty, ...n }); setDlg(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/notebook/${editing.id}`, form);
      else await api.post("/notebook", form);
      toast.success("Nota guardada"); setDlg(false); notify();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const recent = [...notes].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")).slice(0, 5);
  const favorites = notes.filter((n) => n.favorite);
  const pinned = notes.filter((n) => n.pinned);
  const results = q.trim() ? smartSearch(notes, q, ["title", "content", "category"]) : null;

  const NoteRow = ({ n }) => (
    <button onClick={() => openEdit(n)} data-testid="tab-note-row"
      className="w-full text-left flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted/70 transition-colors">
      <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${DOT[n.color] || DOT.amber}`} />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">{n.title || "(sin título)"}</span>
        <span className="block text-[11px] text-muted-foreground truncate">{n.category ? n.category + " · " : ""}{fmtDate(n.updated_at)}</span>
      </span>
      {n.pinned && <Pin className="h-3 w-3 text-primary shrink-0" />}
      {n.favorite && <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />}
    </button>
  );

  const Section = ({ title, items, emptyText }) => (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-2 mb-1">{title}</p>
      {items.length ? items.map((n) => <NoteRow key={n.id} n={n} />) : <p className="text-xs text-muted-foreground px-2 py-1">{emptyText}</p>}
    </div>
  );

  return (
    <>
      {!isOpen && (
        <button className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center justify-center h-16 w-6 rounded-l-lg bg-primary/90 hover:bg-primary hover:w-7 shadow-[0_6px_18px_rgba(0,0,0,0.18)] transition-all duration-200 animate-fade-in"
          data-testid="notebook-tab-handle" aria-label="Abrir Mi Libreta" title="Mi Libreta" onClick={() => setOpen(true)}>
          <StickyNote className="h-3.5 w-3.5 text-primary-foreground" />
        </button>
      )}

      <div ref={panelRef} data-testid="notebook-tab-panel"
        className={`fixed right-0 top-0 bottom-0 z-50 w-[85vw] sm:w-[280px] lg:w-[330px] bg-card border-l border-border shadow-[0_8px_40px_rgba(0,0,0,0.25)] flex flex-col transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between px-4 h-14 border-b border-border bg-primary text-primary-foreground shrink-0">
          <span className="flex items-center gap-2 font-heading font-bold"><StickyNote className="h-5 w-5" /> Mi Libreta</span>
          <button onClick={() => setOpen(false)} data-testid="notebook-tab-close" className="p-1 hover:bg-white/25 rounded-lg transition-colors"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-3 space-y-4 overflow-y-auto flex-1">
          <Button onClick={openCreate} className="w-full rounded-lg gap-2 font-bold uppercase tracking-wider text-xs" data-testid="tab-new-note"><Plus className="h-4 w-4" /> Nueva nota</Button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar título, contenido, categoría..." className="pl-9 rounded-lg h-9 text-sm" data-testid="tab-search" />
          </div>
          {results ? (
            <Section title={`Resultados (${results.length})`} items={results} emptyText="Sin coincidencias" />
          ) : (
            <>
              <Section title="Notas recientes" items={recent} emptyText="Sin notas todavía" />
              <Section title="Favoritas" items={favorites} emptyText="Ninguna favorita" />
              <Section title="Fijadas" items={pinned} emptyText="Ninguna fijada" />
            </>
          )}
        </div>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="rounded-sm max-w-md z-[60]" data-testid="tab-note-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar nota" : "Nueva nota"}</DialogTitle>
            <DialogDescription>Notas privadas, solo tú las ves.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Título" className="rounded-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="tab-note-title" />
            <Textarea placeholder="Escribe tu nota..." className="rounded-sm min-h-[120px]" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="tab-note-content" />
            <Input placeholder="Categoría (opcional)" className="rounded-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="tab-note-category" />
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest">Color</Label>
              <div className="flex gap-2 mt-2">
                {COLOR_KEYS.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, color: c })}
                    className={`h-7 w-7 rounded-sm border-2 ${COLORS[c]} ${form.color === c ? "ring-2 ring-primary ring-offset-1" : ""}`} />
                ))}
              </div>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} /> Fijar</label>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.favorite} onChange={(e) => setForm({ ...form, favorite: e.target.checked })} /> Favorita</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDlg(false)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={save} data-testid="tab-save-note">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
